import { lineString, point } from "@turf/helpers";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import {
  InterpolationSpecType,
  ValueAtProducer,
} from "@orbis-lr-sdk/orbis-lr-sdk";
import { distanceAlongCm, toLatLon } from "../util";

// Distance (cm, on the GK plane) of a map position projected onto a feature's
// line geometry — the linear-reference offset of the cursor along the feature.
export function distanceAlongFeatureCm(feature, position) {
  const latLonGeometry = toLatLon(
    lineString(feature.getGeometry().getCoordinates()).geometry,
  );
  const closestPointLatLon = nearestPointOnLine(
    latLonGeometry,
    toLatLon(point(position).geometry),
  );
  return distanceAlongCm(latLonGeometry, closestPointLatLon);
}

// Crosslink relation features in `allFeatures` that reference `feature` and
// are active at `distanceAtFeatureCm` along it — i.e. the relations under the
// cursor.
export function findCrosslinkRelationsAt(
  feature,
  allFeatures,
  distanceAtFeatureCm,
) {
  const featureId = feature.getId();
  return allFeatures.filter((relation) => {
    const memberIdKeys = memberIdKeysOf(relation);
    if (memberIdKeys.length === 0) {
      return false;
    }
    return memberIdKeys
      .filter((memberIdKey) => relation.get(memberIdKey) === featureId)
      .map((memberIdKey) => relation.get(memberIdKey.replace("-id", "-role")))
      .some((role) => roleInvolvedAt(role, distanceAtFeatureCm));
  });
}

// Whether a relation member's role covers `distanceCm` along the member. A
// plain role (no `=`, no LR) means the member is involved along its whole
// geometry — e.g. a lane fully covered by the crosslink has role "lane" — so it
// matches at any distance. An LR-ed role matches only where its value is
// defined.
function roleInvolvedAt(role, distanceCm) {
  if (typeof role !== "string") {
    return false;
  }
  if (!role.includes("=")) {
    return true;
  }
  const [tagKey, tagValue] = role.split("=", 2);
  return roleValueAt(tagKey, tagValue, distanceCm) !== undefined;
}

// ValueAtProducer.getValueAt throws on step chains when queried before the
// first breakpoint (e.g. distance 0) — it can't build a segment there. Treat a
// throw as "no value at this distance" so a cursor at the start of a line, or
// any unparseable role, doesn't crash the hover/click handlers.
function roleValueAt(tagKey, tagValue, distanceCm) {
  try {
    return ValueAtProducer.getValueAt(
      tagKey,
      tagValue,
      distanceCm,
      InterpolationSpecType.ROLE,
    );
  } catch {
    return undefined;
  }
}

function memberIdKeysOf(relation) {
  return relation
    .getKeys()
    .filter((key) => key.startsWith("relation-member-") && key.endsWith("-id"));
}
